import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Theater, Loader2, LogIn, UserPlus } from "lucide-react";

export default function Login() {
  const { user, login, register } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (user) navigate("/"); }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      if (mode === "login") await login(email, password);
      else await register(email, password, name);
      navigate("/");
    } catch (ex) {
      setErr(formatApiError(ex.response?.data?.detail) || ex.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="relative z-10 flex min-h-screen items-center justify-center px-4 py-10">
      <div className="pointer-events-none absolute inset-0 opacity-40" style={{ background: "radial-gradient(60% 50% at 50% 0%, hsl(38 92% 50% / 0.14), transparent 70%)" }} />
      <div className="relative w-full max-w-md">
        <div className="mb-8 flex flex-col items-center text-center">
          <span className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-primary text-primary-foreground"><Theater className="h-7 w-7" /></span>
          <h1 className="font-mono text-2xl font-extrabold tracking-tight text-foreground">Plantagenet Tech Management</h1>
          <p className="mt-1 text-sm text-muted-foreground">Assets · Maintenance · Faults · Site Surveys</p>
        </div>

        <div className="rounded-2xl border border-border bg-card p-6 sm:p-7">
          <div className="mb-6 grid grid-cols-2 gap-1 rounded-lg bg-secondary/50 p-1">
            {["login", "register"].map((m) => (
              <button
                key={m}
                data-testid={`tab-${m}`}
                onClick={() => { setMode(m); setErr(""); }}
                className={`rounded-md py-2 text-sm font-semibold capitalize tap-scale ${mode === m ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}
              >
                {m === "login" ? "Sign in" : "Register"}
              </button>
            ))}
          </div>

          <form onSubmit={submit} className="space-y-4">
            {mode === "register" && (
              <div>
                <Label className="mb-1.5 block text-xs text-muted-foreground">Name</Label>
                <Input data-testid="login-name" value={name} onChange={(e) => setName(e.target.value)} className="bg-secondary/40" placeholder="Your name" />
              </div>
            )}
            <div>
              <Label className="mb-1.5 block text-xs text-muted-foreground">Email</Label>
              <Input data-testid="login-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="bg-secondary/40" placeholder="you@example.com" />
            </div>
            <div>
              <Label className="mb-1.5 block text-xs text-muted-foreground">Password</Label>
              <Input data-testid="login-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="bg-secondary/40" placeholder="••••••••" />
            </div>
            {err && <p data-testid="login-error" className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">{err}</p>}
            <Button data-testid="login-submit" type="submit" disabled={busy} className="w-full gap-2 bg-primary text-primary-foreground tap-scale hover:bg-primary/90">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : mode === "login" ? <LogIn className="h-4 w-4" /> : <UserPlus className="h-4 w-4" />}
              {mode === "login" ? "Sign in" : "Create account"}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
